/**
 * AudioSystem for the Pacman Animation Version
 *
 * This system handles audio playback for the Pacman game including:
 * - Background gameplay music
 * - Dot collection sound
 * - Power pellet collection sound
 * - Ghost hit sound (for both ghost and power pellet particle hits)
 *
 * Architecture:
 * Game Events → AudioSystem → ResourceManager → HTML5 Audio
 *
 * Key Features:
 * - Background music that loops continuously
 * - Sound effects for game interactions
 * - Volume control for sounds and music
 * - Resource caching via ResourceManager
 * - Error handling for missing audio files
 */

import { System } from '../../../engine/ecs/core/System';
import { Entity } from '../../../engine/ecs/core/Entity';
import { ResourceManager } from '../../../engine/resources/ResourceManager';
import { EventManager } from '../../../engine/events/EventManager';
import { Event } from '../../../engine/events/Event';
import { EventListener } from '../../../engine/events/EventListener';
import { Logger } from '../../../engine/utils/Logger';

export class AudioSystem extends System implements EventListener {
  private resourceManager: ResourceManager;
  private eventManager: EventManager;

  // Audio elements
  private backgroundMusic: HTMLAudioElement | null = null;
  private musicPlaying: boolean = false;

  // Volume settings
  private soundVolume: number = 0.6;
  private musicVolume: number = 0.4;

  // Audio state
  private audioEnabled: boolean = true;

  constructor(resourceManager: ResourceManager) {
    super();
    this.resourceManager = resourceManager;
    this.eventManager = EventManager.getInstance();
    this.logger = Logger.getInstance();

    this.logger.info('AudioSystem: Initialized for Pacman Animation Version');

    // Subscribe to game events
    this.setupEventListeners();
  }

  /**
   * Setup event listeners for game audio triggers
   */
  private setupEventListeners(): void {
    this.eventManager.subscribe('dot_collected', this);
    this.eventManager.subscribe('power_pellet_collected', this);
    this.eventManager.subscribe('ghost_eaten', this);
    this.eventManager.subscribe('pacman_died', this);
    this.eventManager.subscribe('level_complete', this);
    this.eventManager.subscribe('game_over', this);

    this.logger.info('AudioSystem: Event listeners setup complete');
  }

  /**
   * Update method called each frame
   */
  public update(deltaTime: number): void {
    // Start background music if not already playing
    if (this.audioEnabled && !this.musicPlaying) {
      this.startBackgroundMusic();
    }
  }

  /**
   * Handle game events and trigger appropriate sounds
   */
  public onEvent(event: Event): void {
    if (!this.audioEnabled) return;

    switch (event.getType()) {
      case 'dot_collected':
        this.playDotCollectSound();
        break;

      case 'power_pellet_collected':
        this.playPowerPelletSound();
        break;

      case 'ghost_eaten':
        this.playGhostHitSound();
        break;

      case 'pacman_died':
        this.playGhostHitSound(); // Same sound for pacman death
        break;

      case 'level_complete':
        this.playLevelCompleteSound();
        break;

      case 'game_over':
        this.playGameOverSound();
        break;
    }
  }

  /**
   * Play dot collection sound effect
   */
  private playDotCollectSound(): void {
    try {
      const audio = this.resourceManager.loadSound('dot_collect.wav');

      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound
        audio.play().catch((error) => {
          this.logger.warn(
            `AudioSystem: Error playing dot collect sound: ${error}`
          );
        });

        this.logger.debug('AudioSystem: Played dot collect sound');
      } else {
        this.logger.warn('AudioSystem: Could not load dot collect sound file');
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error playing dot collect sound: ${error}`
      );
    }
  }

  /**
   * Play power pellet collection sound effect
   */
  private playPowerPelletSound(): void {
    try {
      const audio = this.resourceManager.loadSound('power_pellet.wav');

      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound
        audio.play().catch((error) => {
          this.logger.warn(
            `AudioSystem: Error playing power pellet sound: ${error}`
          );
        });

        this.logger.debug('AudioSystem: Played power pellet sound');
      } else {
        this.logger.warn('AudioSystem: Could not load power pellet sound file');
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error playing power pellet sound: ${error}`
      );
    }
  }

  /**
   * Play ghost hit sound effect (used for both ghost hits and pacman death)
   */
  private playGhostHitSound(): void {
    try {
      const audio = this.resourceManager.loadSound('ghost_hit.wav');

      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound
        audio.play().catch((error) => {
          this.logger.warn(
            `AudioSystem: Error playing ghost hit sound: ${error}`
          );
        });

        this.logger.debug('AudioSystem: Played ghost hit sound');
      } else {
        this.logger.warn('AudioSystem: Could not load ghost hit sound file');
      }
    } catch (error) {
      this.logger.warn(`AudioSystem: Error playing ghost hit sound: ${error}`);
    }
  }

  /**
   * Play level complete sound effect
   */
  private playLevelCompleteSound(): void {
    try {
      const audio = this.resourceManager.loadSound('level_complete.wav');

      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound
        audio.play().catch((error) => {
          this.logger.warn(
            `AudioSystem: Error playing level complete sound: ${error}`
          );
        });

        this.logger.debug('AudioSystem: Played level complete sound');
      } else {
        this.logger.warn(
          'AudioSystem: Could not load level complete sound file'
        );
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error playing level complete sound: ${error}`
      );
    }
  }

  /**
   * Play game over sound effect
   */
  private playGameOverSound(): void {
    try {
      const audio = this.resourceManager.loadSound('game_over.wav');

      if (audio) {
        // Reset audio to beginning
        audio.currentTime = 0;

        // Set volume
        audio.volume = this.soundVolume;

        // Play sound
        audio.play().catch((error) => {
          this.logger.warn(
            `AudioSystem: Error playing game over sound: ${error}`
          );
        });

        this.logger.debug('AudioSystem: Played game over sound');
      } else {
        this.logger.warn('AudioSystem: Could not load game over sound file');
      }
    } catch (error) {
      this.logger.warn(`AudioSystem: Error playing game over sound: ${error}`);
    }
  }

  /**
   * Start background music playback
   */
  private startBackgroundMusic(): void {
    if (this.musicPlaying) {
      this.logger.debug('AudioSystem: Background music already playing');
      return;
    }

    try {
      this.backgroundMusic = this.resourceManager.loadMusic(
        'pacman_background.wav'
      );

      if (this.backgroundMusic) {
        // Set volume
        this.backgroundMusic.volume = this.musicVolume;

        // Set to loop continuously
        this.backgroundMusic.loop = true;

        // Play music
        this.backgroundMusic
          .play()
          .then(() => {
            this.musicPlaying = true;
            this.logger.info('AudioSystem: Started background music');
          })
          .catch((error) => {
            this.logger.warn(
              `AudioSystem: Error starting background music: ${error}`
            );
          });
      } else {
        this.logger.warn('AudioSystem: Could not load background music file');
      }
    } catch (error) {
      this.logger.warn(
        `AudioSystem: Error starting background music: ${error}`
      );
    }
  }

  /**
   * Stop background music playback
   */
  private stopBackgroundMusic(): void {
    if (!this.musicPlaying) {
      this.logger.debug('AudioSystem: Background music not playing');
      return;
    }

    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }

    this.musicPlaying = false;
    this.logger.info('AudioSystem: Stopped background music');
  }

  /**
   * Toggle audio on/off
   */
  public toggleAudio(): void {
    this.audioEnabled = !this.audioEnabled;

    if (!this.audioEnabled) {
      this.stopBackgroundMusic();
    } else if (!this.musicPlaying) {
      this.startBackgroundMusic();
    }

    this.logger.info(
      `AudioSystem: Audio ${this.audioEnabled ? 'enabled' : 'disabled'}`
    );
  }

  /**
   * Set sound volume (0.0 to 1.0)
   */
  public setSoundVolume(volume: number): void {
    this.soundVolume = Math.max(0, Math.min(1, volume));
    this.logger.info(`AudioSystem: Sound volume set to ${this.soundVolume}`);
  }

  /**
   * Set music volume (0.0 to 1.0)
   */
  public setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));

    if (this.backgroundMusic) {
      this.backgroundMusic.volume = this.musicVolume;
    }

    this.logger.info(`AudioSystem: Music volume set to ${this.musicVolume}`);
  }

  /**
   * Get current audio state
   */
  public isAudioEnabled(): boolean {
    return this.audioEnabled;
  }

  /**
   * Get current sound volume
   */
  public getSoundVolume(): number {
    return this.soundVolume;
  }

  /**
   * Get current music volume
   */
  public getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * Clean up audio resources
   */
  public cleanup(): void {
    this.stopBackgroundMusic();
    this.resourceManager.clearAudioCache();
    this.logger.info('AudioSystem: Cleaned up audio resources');
  }
}
